import React, { useState } from 'react';
import { Save, RotateCcw, Eye, EyeOff } from 'lucide-react';
import { useColumnSettings, ColumnSettings } from '@/hooks/useColumnSettings';
import { useToast } from '@/components/ui/Toast';

type ViewType = 'orders' | 'offers' | 'production';

interface ColumnConfig {
  key: keyof ColumnSettings;
  label: string;
  description?: string;
}

const viewConfigs: Record<ViewType, { label: string; columns: ColumnConfig[] }> = {
  orders: {
    label: 'Zamówienia',
    columns: [
      { key: 'show_order_name', label: 'Nazwa zamówienia', description: 'Wyświetl nazwę i numer zamówienia' },
      { key: 'show_delivery_date', label: 'Data dostawy', description: 'Wyświetl planowaną datę dostawy' },
      { key: 'show_client_name', label: 'Nazwa klienta', description: 'Wyświetl nazwę klienta' },
      { key: 'show_quantity', label: 'Ilość', description: 'Wyświetl ilość sztuk' },
      { key: 'show_status', label: 'Status', description: 'Wyświetl aktualny status zamówienia' },
      { key: 'show_value', label: 'Wartość', description: 'Wyświetl wartość zamówienia' }
    ]
  },
  offers: {
    label: 'Oferty',
    columns: [
      { key: 'show_offer_number', label: 'Numer oferty', description: 'Wyświetl numer oferty' },
      { key: 'show_client_name', label: 'Nazwa klienta', description: 'Wyświetl nazwę klienta' },
      { key: 'show_valid_until', label: 'Ważna do', description: 'Wyświetl datę ważności oferty' },
      { key: 'show_status', label: 'Status', description: 'Wyświetl status oferty' },
      { key: 'show_value', label: 'Wartość', description: 'Wyświetl wartość oferty' },
      { key: 'show_created_date', label: 'Data utworzenia', description: 'Wyświetl datę utworzenia oferty' }
    ]
  },
  production: {
    label: 'Produkcja',
    columns: [
      { key: 'show_order_name', label: 'Nazwa zlecenia', description: 'Wyświetl nazwę zlecenia produkcyjnego' },
      { key: 'show_machine', label: 'Maszyna', description: 'Wyświetl przypisaną maszynę' },
      { key: 'show_operator', label: 'Operator', description: 'Wyświetl przypisanego operatora' },
      { key: 'show_start_time', label: 'Czas rozpoczęcia', description: 'Wyświetl czas rozpoczęcia' },
      { key: 'show_end_time', label: 'Czas zakończenia', description: 'Wyświetl czas zakończenia' },
      { key: 'show_status', label: 'Status', description: 'Wyświetl status produkcji' }
    ]
  }
};

export default function ColumnSettingsManager() {
  const { columnSettings, saveSettings, resetSettings, loading } = useColumnSettings();
  const { showToast } = useToast();
  const [activeView, setActiveView] = useState<ViewType>('orders');
  const [localSettings, setLocalSettings] = useState(columnSettings);
  const [hasChanges, setHasChanges] = useState(false);

  const handleToggle = (key: keyof ColumnSettings) => {
    const newSettings = {
      ...localSettings,
      [activeView]: {
        ...localSettings[activeView],
        [key]: !localSettings[activeView][key]
      }
    };
    setLocalSettings(newSettings);
    setHasChanges(true);
  };

  const handleSave = async () => {
    const result = await saveSettings(activeView, localSettings[activeView]);
    if (result.success) {
      showToast({
        type: 'success',
        title: 'Ustawienia zapisane',
        message: 'Ustawienia kolumn zostały zapisane'
      });
      setHasChanges(false);
    } else {
      showToast({
        type: 'error',
        title: 'Błąd',
        message: 'Błąd podczas zapisywania ustawień'
      });
    }
  };

  const handleReset = async () => {
    if (confirm(`Czy na pewno chcesz przywrócić domyślne ustawienia dla widoku "${viewConfigs[activeView].label}"?`)) {
      const result = await resetSettings(activeView);
      if (result.success) {
        showToast({
          type: 'success',
          title: 'Ustawienia przywrócone',
          message: 'Przywrócono domyślne ustawienia'
        });
        setLocalSettings(prev => ({
          ...prev,
          [activeView]: columnSettings[activeView]
        }));
        setHasChanges(false);
      } else {
        showToast({
          type: 'error',
          title: 'Błąd',
          message: 'Błąd podczas resetowania ustawień'
        });
      }
    }
  };

  const toggleAll = (show: boolean) => {
    const allSettings: any = {};
    viewConfigs[activeView].columns.forEach(col => {
      allSettings[col.key] = show;
    });
    
    const newSettings = {
      ...localSettings,
      [activeView]: {
        ...localSettings[activeView],
        ...allSettings
      }
    };
    setLocalSettings(newSettings);
    setHasChanges(true);
  };

  return (
    <div className="max-w-4xl">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Ustawienia wyświetlania kolumn
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Dostosuj które kolumny są widoczne w tabelach
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px">
            {Object.entries(viewConfigs).map(([key, config]) => (
              <button
                key={key}
                onClick={() => {
                  if (hasChanges) {
                    if (confirm('Masz niezapisane zmiany. Czy chcesz je porzucić?')) {
                      setActiveView(key as ViewType);
                      setLocalSettings(columnSettings);
                      setHasChanges(false);
                    }
                  } else {
                    setActiveView(key as ViewType);
                  }
                }}
                className={`
                  px-6 py-3 text-sm font-medium border-b-2 transition-colors
                  ${activeView === key
                    ? 'text-indigo-600 dark:text-indigo-400 border-indigo-600 dark:border-indigo-400'
                    : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-300'
                  }
                `}
              >
                {config.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Quick actions */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => toggleAll(true)}
              className="text-sm px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-1.5"
            >
              <Eye className="w-4 h-4" />
              Pokaż wszystkie
            </button>
            <button
              onClick={() => toggleAll(false)}
              className="text-sm px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-1.5"
            >
              <EyeOff className="w-4 h-4" />
              Ukryj wszystkie
            </button>
          </div>

          {/* Column toggles */}
          <div className="space-y-3">
            {viewConfigs[activeView].columns.map((column) => (
              <label
                key={column.key}
                className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={localSettings[activeView][column.key] !== false}
                  onChange={() => handleToggle(column.key)}
                  className="mt-1 w-4 h-4 text-indigo-600 rounded border-gray-300 dark:border-gray-600 focus:ring-indigo-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-white">
                    {column.label}
                  </div>
                  {column.description && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {column.description}
                    </div>
                  )}
                </div>
              </label>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleSave}
              disabled={loading || !hasChanges}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              Zapisz zmiany
            </button>
            <button
              onClick={handleReset}
              disabled={loading}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Przywróć domyślne
            </button>
          </div>

          {hasChanges && (
            <div className="mt-3 text-sm text-amber-600 dark:text-amber-400 text-center">
              Masz niezapisane zmiany
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
